import {useFormik} from 'formik';
import {useState} from 'react';
import {
  CreateCommunicationMessageDTO,
  CommunicationGroup,
  CommunicationMediaType,
  CommunicationVideoStyle,
  RecipientSelector,
  useCommunicationMessageCreate,
} from '../../../entities';
import * as Yup from 'yup';
import {AxiosError} from 'axios';

type UseAddCommunicationMessageFormProps = {
  setOpen: (open: boolean) => void;
};

export const useAddCommunicationMessageForm = ({setOpen}: UseAddCommunicationMessageFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {createMessageMutation} = useCommunicationMessageCreate();

  const onSubmit = async (values: CreateCommunicationMessageDTO) => {
    setErrorMessage(null);
    try {
      await createMessageMutation.mutateAsync(values);
      setOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.detail || 'Произошла ошибка при создании уведомления');
    }
  };

  const formik = useFormik({
    initialValues: {
      subject: '',
      body: '',
      group: 'ProjectNews' as CommunicationGroup,
      button: null as {text: string; link: string} | null,
      showButton: false,
      mediaType: 'image' as CommunicationMediaType,
      imageUploadId: null as string | null,
      imagePreview: null as string | null,
      videoUploadId: null as string | null,
      videoPreview: null as string | null,
      videoStyle: 'Circle' as CommunicationVideoStyle,
      recipientsSelector: 'All' as RecipientSelector,
      recipientsFileUploadId: null as string | null,
    },
    validationSchema: Yup.object({
      subject: Yup.string()
        .min(1, 'Тема не может быть пустой')
        .max(255, 'Тема не должна превышать 255 символов')
        .required('Тема обязательна'),
      body: Yup.string().min(1, 'Текст не может быть пустым').required('Текст обязателен'),
      group: Yup.string()
        .oneOf(['ProjectNews', 'AppealsAndReports', 'Ambassadors', 'TechnicalSupport'])
        .required('Группа обязательна'),
      button: Yup.object().when('showButton', {
        is: true,
        then: (schema) =>
          schema
            .shape({
              text: Yup.string().min(1, 'Текст кнопки обязателен').required('Текст кнопки обязателен'),
              link: Yup.string()
                .min(1, 'Ссылка обязательна')
                .url('Неверный формат ссылки')
                .required('Ссылка обязательна'),
            })
            .required(),
        otherwise: (schema) => schema.nullable(),
      }),
      recipientsSelector: Yup.string()
        .oneOf([
          'FromFile',
          'All',
          'Personal',
          'FromAppeal',
          'NeverDonated',
          'DonatedOnce',
          'DonatedTwice',
          'DonatedThreeOrMoreTimes',
          'WithActiveSubscription',
          'WithoutActiveSubscription',
          'WithoutActiveButWithInactiveSubscription',
        ])
        .required('Получатели обязательны'),
      recipientsFileUploadId: Yup.string().when('recipientsSelector', {
        is: 'FromFile',
        then: (schema) => schema.required('Файл с получателями обязателен'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    onSubmit: (values) => {
      // Формируем media объект в зависимости от типа
      let media: CreateCommunicationMessageDTO['media'] = null;
      if (values.mediaType === 'image' && values.imageUploadId) {
        media = {type: 'image', imageUploadId: values.imageUploadId};
      } else if (values.mediaType === 'video' && values.videoUploadId) {
        media = {type: 'video', videoUploadId: values.videoUploadId, style: values.videoStyle};
      }

      const submitData: CreateCommunicationMessageDTO = {
        subject: values.subject,
        body: values.body,
        group: values.group,
        button: values.showButton ? values.button : null,
        media,
        recipientsSelector: values.recipientsSelector,
        recipientsFileUploadId: values.recipientsFileUploadId,
      };
      onSubmit(submitData);
    },
  });

  return {
    ...formik,
    isLoading: createMessageMutation.isPending,
    submitError: createMessageMutation.error
      ? (createMessageMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    errorMessage,
    submitForm: () => formik.handleSubmit(),
  };
};
